import { type Event, trackEvents } from '@/utils/analytics';
import { EXPERIMENT_BUCKET_COOKIES } from '@/utils/featureFlags/constants';
import { readRolloutBucket } from '@/utils/featureFlags/rolloutFeatureFlags';
import type { PublicRetrofitOrderSummary } from '@/utils/publicBatteryEcomm/orderSummary';
import {
  BATTERY_BUNDLE_TAB_ID,
  BATTERY_ONLY_TAB_ID,
  type PublicRetrofitProductType,
} from '@/utils/publicBatteryEcomm/productType';

const FLOW = 'public_retrofit';
const CURRENCY = 'USD';

export const PUBLIC_RETROFIT_GTM_EVENT = {
  BUCKET_ASSIGNED: 'public_retrofit_bucket_assigned',
  PAYMENT_VARIANT_ASSIGNED: 'public_retrofit_payment_variant_assigned',
  LANDING_LOADED: 'public_retrofit_landing_loaded',
  ADDRESS_FOCUSED: 'public_retrofit_address_focused',
  ADDRESS_SELECTED: 'public_retrofit_address_selected',
  UTILITY_REQUIRED: 'public_retrofit_utility_required',
  UTILITY_SELECTED: 'public_retrofit_utility_selected',
  UTILITY_UNSUPPORTED: 'public_retrofit_utility_unsupported',
  LANDING_SUBMITTED: 'public_retrofit_landing_submitted',
  OUT_OF_SERVICE: 'public_retrofit_out_of_service',
  PRODUCTS_ERROR: 'public_retrofit_products_error',
  QUOTE_LOADED: 'public_retrofit_quote_loaded',
  PRODUCT_TYPE_SELECTED: 'public_retrofit_product_type_selected',
  QUANTITY_SELECTED: 'public_retrofit_quantity_selected',
  EDIT_ADDRESS: 'public_retrofit_edit_address',
  ORDER_SUBMITTED: 'public_retrofit_order_submitted',
  ORDER_PLACED: 'public_retrofit_order_placed',
  ORDER_ERROR: 'public_retrofit_order_error',
  CONFIRMATION_LOADED: 'public_retrofit_confirmation_loaded',
  HELP_CONTACT_CLICKED: 'public_retrofit_help_contact_clicked',
} as const;

const PUBLIC_RETROFIT_OUT_OF_SERVICE_DESTINATION = 'add_a_battery';

export const PUBLIC_RETROFIT_VARIANT = {
  RETROFIT: 'retrofit',
  LEGACY: 'legacy_add_a_battery',
} as const;

export type PublicRetrofitVariant =
  (typeof PUBLIC_RETROFIT_VARIANT)[keyof typeof PUBLIC_RETROFIT_VARIANT];
export type PublicRetrofitPaymentVariant = 'skip' | 'collect';
export type PublicRetrofitUtilitySelection = 'auto' | 'user';
export type PublicRetrofitOutOfServiceReason =
  | 'unserved_state'
  | 'not_in_market_rollout'
  | 'not_in_market'
  | 'unsupported_utility'
  | 'tx_zip_not_allowed'
  | 'ineligible_by_api';
export type PublicRetrofitOrderErrorType =
  | 'missing_products'
  | 'proposal_api'
  | 'lead_api';

export type PublicRetrofitOrderDetails = PublicRetrofitOrderSummary & {
  escalator?: number;
  pricePointId?: string;
  model?: string;
  manufacturer?: string;
};

const PRODUCT_TYPE_PARAM: Record<PublicRetrofitProductType, string> = {
  [BATTERY_BUNDLE_TAB_ID]: 'battery_bundle',
  [BATTERY_ONLY_TAB_ID]: 'battery_only',
};

function toProductTypeParam(productType: PublicRetrofitProductType): string {
  return PRODUCT_TYPE_PARAM[productType];
}

export type PublicRetrofitEventContext = {
  zip?: string;
  state?: string;
  utility?: string;
  productType?: PublicRetrofitProductType;
};

function toContextParams({
  zip,
  state,
  utility,
  productType,
}: PublicRetrofitEventContext) {
  return {
    ...(zip && { zip }),
    ...(state && { state: state.toUpperCase() }),
    ...(utility && { utility }),
    ...(productType && { product_type: toProductTypeParam(productType) }),
  };
}

// GA stores the client_id as the last two segments of the `_ga` cookie (GA1.1.<id>.<timestamp>).
function readAnonymousId(): string | undefined {
  const gaCookie = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith('_ga='));
  const segments = gaCookie?.split('=')[1]?.split('.');
  if (!segments || segments.length < 4) return undefined;
  return segments.slice(-2).join('.');
}

/**
 * Params every retrofit event carries so reports can segment the whole funnel
 * by rollout bucket without each call site threading it through.
 */
function track(event: string, params: Omit<Event, 'event'> = {}): void {
  const rolloutBucket = readRolloutBucket(
    EXPERIMENT_BUCKET_COOKIES.PUBLIC_RETROFIT
  );

  const anonymousId = readAnonymousId();

  trackEvents({
    event,
    event_id: crypto.randomUUID(),
    flow: FLOW,
    ...(anonymousId && { anonymous_id: anonymousId }),
    ...(rolloutBucket !== undefined && { rollout_bucket: rolloutBucket }),
    ...params,
  });
}

export function trackPublicRetrofitBucketAssigned({
  bucket,
  rolloutPercent,
  state,
  inRollout,
}: {
  bucket: number | undefined;
  rolloutPercent: number;
  state: string;
  inRollout: boolean;
}) {
  track(PUBLIC_RETROFIT_GTM_EVENT.BUCKET_ASSIGNED, {
    ...(bucket !== undefined && { rollout_bucket: bucket }),
    rollout_percent: rolloutPercent,
    state: state.toUpperCase(),
    in_rollout: inRollout,
    variant: inRollout
      ? PUBLIC_RETROFIT_VARIANT.RETROFIT
      : PUBLIC_RETROFIT_VARIANT.LEGACY,
  });
}

export function trackPublicRetrofitPaymentVariantAssigned(
  paymentVariant: PublicRetrofitPaymentVariant
) {
  track(PUBLIC_RETROFIT_GTM_EVENT.PAYMENT_VARIANT_ASSIGNED, {
    payment_variant: paymentVariant,
  });
}

export function trackPublicRetrofitLandingLoaded() {
  track(PUBLIC_RETROFIT_GTM_EVENT.LANDING_LOADED);
}

export function trackPublicRetrofitAddressFocused() {
  track(PUBLIC_RETROFIT_GTM_EVENT.ADDRESS_FOCUSED, { page: 'landing' });
}

export function trackPublicRetrofitAddressSelected({
  zip,
  state,
}: {
  zip: string;
  state: string;
}) {
  track(PUBLIC_RETROFIT_GTM_EVENT.ADDRESS_SELECTED, {
    zip,
    state: state.toUpperCase(),
  });
}

export function trackPublicRetrofitUtilityRequired(zip: string) {
  track(PUBLIC_RETROFIT_GTM_EVENT.UTILITY_REQUIRED, { zip });
}

export function trackPublicRetrofitUtilitySelected({
  utility,
  selection,
}: {
  utility: string;
  selection: PublicRetrofitUtilitySelection;
}) {
  track(PUBLIC_RETROFIT_GTM_EVENT.UTILITY_SELECTED, {
    utility,
    selection,
  });
}

export function trackPublicRetrofitUtilityUnsupported(utility: string) {
  track(PUBLIC_RETROFIT_GTM_EVENT.UTILITY_UNSUPPORTED, { utility });
}

export function trackPublicRetrofitLandingSubmitted({
  state,
  utility,
}: {
  state: string;
  utility: string;
}) {
  track(PUBLIC_RETROFIT_GTM_EVENT.LANDING_SUBMITTED, {
    state: state.toUpperCase(),
    utility,
  });
}

export function trackPublicRetrofitOutOfService({
  reason,
  zip,
  state,
  utility,
}: {
  reason: PublicRetrofitOutOfServiceReason;
  /** Omitted by the rollout gate, which redirects before an address is entered */
  zip?: string;
  state: string;
  /** Omitted by the rollout gate, which redirects before a utility is known */
  utility?: string;
}) {
  track(PUBLIC_RETROFIT_GTM_EVENT.OUT_OF_SERVICE, {
    reason,
    destination: PUBLIC_RETROFIT_OUT_OF_SERVICE_DESTINATION,
    ...(zip && { zip }),
    state: state.toUpperCase(),
    ...(utility !== undefined && { utility }),
  });
}

export function trackPublicRetrofitProductsError({
  state,
  utility,
}: {
  state: string;
  utility: string;
}) {
  track(PUBLIC_RETROFIT_GTM_EVENT.PRODUCTS_ERROR, {
    state: state.toUpperCase(),
    utility,
  });
}

export function trackPublicRetrofitQuoteLoaded({
  productType,
  model,
  manufacturer,
  quantity,
  monthlyAmount,
  leaseLength,
  state,
  utility,
}: {
  productType: PublicRetrofitProductType;
  model: string | undefined;
  manufacturer: string | undefined;
  quantity: number;
  monthlyAmount: number;
  leaseLength: number;
  state: string;
  utility: string;
}) {
  track(PUBLIC_RETROFIT_GTM_EVENT.QUOTE_LOADED, {
    product_type: toProductTypeParam(productType),
    ...(model && { model }),
    ...(manufacturer && { manufacturer }),
    battery_count: quantity,
    monthly_amount: monthlyAmount,
    lease_length: leaseLength,
    state: state.toUpperCase(),
    utility,
  });
}

export function trackPublicRetrofitProductTypeSelected(
  productType: PublicRetrofitProductType
) {
  track(PUBLIC_RETROFIT_GTM_EVENT.PRODUCT_TYPE_SELECTED, {
    product_type: toProductTypeParam(productType),
  });
}

export function trackPublicRetrofitQuantitySelected({
  productType,
  quantity,
  monthlyAmount,
}: {
  productType: PublicRetrofitProductType;
  quantity: number;
  monthlyAmount: number | undefined;
}) {
  track(PUBLIC_RETROFIT_GTM_EVENT.QUANTITY_SELECTED, {
    product_type: toProductTypeParam(productType),
    battery_count: quantity,
    ...(monthlyAmount !== undefined && { monthly_amount: monthlyAmount }),
  });
}

export function trackPublicRetrofitEditAddress(
  context: PublicRetrofitEventContext
) {
  track(PUBLIC_RETROFIT_GTM_EVENT.EDIT_ADDRESS, toContextParams(context));
}

export function trackPublicRetrofitOrderSubmitted({
  productType,
  quantity,
  monthlyAmount,
  escalator,
  pricePointId,
}: Partial<PublicRetrofitOrderDetails> & {
  productType: PublicRetrofitProductType;
}) {
  track(PUBLIC_RETROFIT_GTM_EVENT.ORDER_SUBMITTED, {
    product_type: toProductTypeParam(productType),
    ...(quantity !== undefined && { battery_count: quantity }),
    ...(monthlyAmount !== undefined && { monthly_amount: monthlyAmount }),
    ...(escalator !== undefined && { escalator }),
    ...(pricePointId && { price_point_id: pricePointId }),
  });
}

export function trackPublicRetrofitOrderPlaced({
  productType,
  quantity,
  monthlyAmount,
  escalator,
  pricePointId,
  model,
  manufacturer,
}: PublicRetrofitOrderDetails) {
  track(PUBLIC_RETROFIT_GTM_EVENT.ORDER_PLACED, {
    product_type: toProductTypeParam(productType),
    battery_count: quantity,
    monthly_amount: monthlyAmount,
    ...(escalator !== undefined && { escalator }),
    ...(pricePointId && { price_point_id: pricePointId }),
    ...(model && { model }),
    ...(manufacturer && { manufacturer }),
    value: monthlyAmount,
    currency: CURRENCY,
  });
}

export function trackPublicRetrofitOrderError({
  errorType,
  productType,
}: {
  errorType: PublicRetrofitOrderErrorType;
  productType: PublicRetrofitProductType;
}) {
  track(PUBLIC_RETROFIT_GTM_EVENT.ORDER_ERROR, {
    error_type: errorType,
    product_type: toProductTypeParam(productType),
  });
}

export function trackPublicRetrofitConfirmationLoaded(
  order: PublicRetrofitOrderSummary | undefined
) {
  track(PUBLIC_RETROFIT_GTM_EVENT.CONFIRMATION_LOADED, {
    ...(order && {
      product_type: toProductTypeParam(order.productType),
      battery_count: order.quantity,
      monthly_amount: order.monthlyAmount,
    }),
  });
}

export function trackPublicRetrofitHelpContactClicked({
  page,
  ...context
}: PublicRetrofitEventContext & { page: string }) {
  track(PUBLIC_RETROFIT_GTM_EVENT.HELP_CONTACT_CLICKED, {
    page,
    ...toContextParams(context),
  });
}
